import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import {
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { forkJoin, map, Observable, startWith } from "rxjs";
import { WriterService } from "../../services/writer.service";
import { optionValidator, ReaderService } from "../../services/reader.service";
import { AuthenticationService } from "../authentication/authentication/authentication.service";
import { MatSort } from "@angular/material/sort";
import {
  Electrification,
  Messages,
  MyErrorStateMatcher,
  Vehicle,
} from "../utility/utility-classes";
import { ErrorHandlerDialogComponent } from "../error-handler-dialog/error-handler-dialog.component";
import Quill from "quill";
import { QuillTableEmbed } from "../base-components/quill-table-embed";
import { ConfirmDeletionDialogComponent } from "../utility/confirm-deletion-dialog/confirm-deletion-dialog.component";
import { Router } from "@angular/router";
import * as FileSaver from "file-saver";

@Component({
    selector: "app-new-vehicle",
    templateUrl: "./new-vehicle.component.html",
    styleUrls: ["./new-vehicle.component.scss"],
    standalone: false
})
export class NewVehicleComponent implements OnInit, OnDestroy {
  matcher = new MyErrorStateMatcher();
  vehicle: Vehicle = new Vehicle();
  vehicleForm: UntypedFormGroup;
  oems: string[];
  brands: string[];
  architectures: string[];
  filteredOEMs: Observable<string[]>;
  filteredBrands: Observable<string[]>;
  engineeringClasses: string[];
  driveConfigurations: string[];
  filteredArchitectures: Observable<string[]>;
  architectureTypes: string[];
  thirdRowSeats: string[] = ["YES", "NO", "N/A", "TBD"];
  globalSegments: string[];
  launchProbabilities: string[];
  electricTypes: string[];
  complete = false;
  createMode = false;
  minStartYear = 1980;
  isLoading = false;

  isImage: boolean;

  quillConfiguration = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic"],
      ["link"],
      [{ list: "bullet" }, { list: "ordered" }],
      [{ indent: "+1" }, { indent: "-1" }],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild("editor") quill: Quill;

  constructor(
    public dialogRef: MatDialogRef<NewVehicleComponent>,
    public readerService: ReaderService,
    private readonly router: Router,
    private writerService: WriterService,
    private authService: AuthenticationService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private data: any
  ) {
    Quill.register(QuillTableEmbed, true);

    if (data != null) {
      this.createMode = data.createMode;
      this.vehicle = data;
      // Clear appropriate checkboxes
      this.vehicle.active = this.vehicle.active === "Active" || this.createMode;
      this.vehicle.performance = this.vehicle.performance === "Yes";
      this.vehicle.crossvariant = this.vehicle.crossvariant === "Yes";
    } else {
      this.vehicle.active = true;
    }
    this.vehicleForm = new UntypedFormGroup({
      id: new UntypedFormControl(this.vehicle.id),
      oem: new UntypedFormControl(this.vehicle.oem, [Validators.required]),
      brand: new UntypedFormControl(this.vehicle.brand, [Validators.required]),
      nameplate: new UntypedFormControl(this.vehicle.nameplate, [
        Validators.required,
        Validators.pattern(/[\S]/),
      ]),
      globalnameplate: new UntypedFormControl(this.vehicle.globalnameplate),
      active: new UntypedFormControl(this.vehicle.active),
      yearbegin: new UntypedFormControl(this.vehicle.yearbegin, [
        Validators.required,
        Validators.pattern("[0-9]{4}"),
      ]),
      yearend: new UntypedFormControl(this.vehicle.yearend, [
        Validators.required,
        Validators.pattern("[0-9]{4}"),
      ]),
      engineeringclass: new UntypedFormControl(this.vehicle.engineeringclass, [
        Validators.required,
      ]),
      driveconfig: new UntypedFormControl(this.vehicle.driveconfig),
      architecture: new UntypedFormControl(this.vehicle.architecture),
      architecturetype: new UntypedFormControl(this.vehicle.architecturetype),
      programcode: new UntypedFormControl(this.vehicle.programcode),
      thirdrowseat: new UntypedFormControl(this.vehicle.thirdrowseat || "TBD", [
        Validators.required,
      ]),
      globalsegment: new UntypedFormControl(this.vehicle.globalsegment, [
        Validators.required,
      ]),
      launchprobability: new UntypedFormControl(
        this.vehicle.launchprobability || "Confirmed",
        [Validators.required]
      ),
      propulsion: new UntypedFormGroup(
        {
          ice: new UntypedFormControl(this.vehicle.ice),
          electric: new UntypedFormControl(this.vehicle.electric),
          electriconly: new UntypedFormControl(this.vehicle.electriconly),
          hybrid: new UntypedFormControl(this.vehicle.hybrid),
          mildhybrid24volts: new UntypedFormControl(this.vehicle.mildhybrid24volts),
          pluginhybrid: new UntypedFormControl(this.vehicle.pluginhybrid),
          extendedrange: new UntypedFormControl(this.vehicle.extendedrange),
          fuelcell: new UntypedFormControl(this.vehicle.fuelcell),
          fuelcellplugin: new UntypedFormControl(this.vehicle.fuelcellplugin),
        },
        { validators: this.atLeastOneCheckboxSelected() }
      ),
      performance: new UntypedFormControl(this.vehicle.performance || false),
      performancenameplate: new UntypedFormControl(
        this.vehicle.performancenameplate
      ),
      crossvariant: new UntypedFormControl(this.vehicle.crossvariant || false),
      crossvariantnameplate: new UntypedFormControl(
        this.vehicle.crossvariantnameplate
      ),
      autonomouslevel: new UntypedFormControl(this.vehicle.autonomouslevel),
      isautonomous: new UntypedFormControl(this.vehicle.isautonomous),
      autonomousprobability: new UntypedFormControl(
        this.vehicle.autonomousprobability
      ),
      autonomousyear: new UntypedFormControl(this.vehicle.autonomousyear),
      autonomousdescription: new UntypedFormControl(
        this.vehicle.autonomousdescription
      ),
      highlights: new UntypedFormControl(this.vehicle.highlights),
      powertrain: new UntypedFormControl(this.vehicle.powertrain),
      created: new UntypedFormControl(),
      createdby: new UntypedFormControl(),
    });
  }

  ngOnInit(): void {
    if (this.quill) {
      const tableModule = this.quill.getModule("better-table");
      const box: HTMLElement = document.body.querySelector(
        "#insert-table"
      ) as HTMLElement | null;
      box.onclick = () => {
        tableModule.insertTableUsingGrid(3, 3);
      };

      const box2: HTMLElement = document.body.querySelector(
        "#get-table"
      ) as HTMLElement | null;

      box2.onclick = () => {
        console.log(tableModule.getTable());
      };

      const box3: HTMLElement = document.body.querySelector(
        "#get-contents"
      ) as HTMLElement | null;

      box3.onclick = () => {
        console.log(this.quill.getContents());
      };
    }
    this.isImage = false;
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.vehicle.active = this.vehicle.active ? "Yes" : "No";
    this.vehicle.performance = this.vehicle.performance ? "Yes" : "No";
    this.vehicle.crossvariant = this.vehicle.crossvariant ? "Yes" : "No";
  }

  private atLeastOneCheckboxSelected(): ValidatorFn {
    return (control: UntypedFormGroup): ValidationErrors | null => {
      const atLeastOneSelected = Object.values(control.value).some(
        (value) => value === true || value === "X"
      );
      return atLeastOneSelected ? null : { atLeastOneCheckboxSelected: true };
    };
  }

  private loadInitialData(): void {
    this.readerService.getAllEnabledFromTable("oem").subscribe((x) => {
      this.oems = x.map((obj) => obj.name);
      this.vehicleForm.get("oem").setValidators(optionValidator(this.oems));
      this.filteredOEMs = this.vehicleForm.get("oem").valueChanges.pipe(
        startWith(""),
        map((value) => this.readerService._filterAutoComplete(value, this.oems))
      );
    });
    this.readerService.getAllEnabledFromTable("brand").subscribe((x) => {
      this.brands = x.map((obj) => obj.name);
      this.vehicleForm.get("brand").setValidators(optionValidator(this.brands));
      this.filteredBrands = this.vehicleForm.get("brand").valueChanges.pipe(
        startWith(""),
        map((value) =>
          this.readerService._filterAutoComplete(value, this.brands)
        )
      );
    });

    this.readerService.getAllEnabledFromTable("architecture").subscribe((x) => {
      this.architectures = x.map((obj) => obj.name);
      this.vehicleForm
        .get("architecture")
        .setValidators(optionValidator(this.architectures));
      this.filteredArchitectures = this.vehicleForm
        .get("architecture")
        .valueChanges.pipe(
          startWith(""),
          map((value) =>
            this.readerService._filterAutoComplete(value, this.architectures)
          )
        );
    });

    this.readerService
      .getAllEnabledFromTable("globalsegment")
      .subscribe((x: any[]) => {
        this.globalSegments = x.map((obj) => obj.name);
      });
    this.readerService
      .getAllEnabledFromTable("engineeringclass")
      .subscribe((x: any[]) => {
        this.engineeringClasses = x.map((obj) => obj.name);
      });
    this.readerService
      .getAllEnabledFromTable("driveconfig")
      .subscribe((x: any[]) => {
        this.driveConfigurations = x.map((obj) => obj.name);
      });
    this.readerService
      .getAllEnabledFromTable("architecturetype")
      .subscribe((x: any[]) => {
        this.architectureTypes = x.map((obj) => obj.name);
      });
    this.readerService
      .getAllEnabledFromTable("launchprobability")
      .subscribe((x: any[]) => {
        this.launchProbabilities = x.map((obj) => obj.name);
      });
    this.readerService
      .getDistinctFromTable("electrification", "type")
      .subscribe((x: any[]) => {
        this.electricTypes = x;
      });

    this.vehicleForm.markAllAsTouched();
    if (this.vehicleForm.get("yearbegin").value === 1111) {
      this.minStartYear = 1111;
    }
    if (this.vehicleForm.get("yearbegin").value === 1980) {
      this.minStartYear = 1979;
    }
  }

  public validateUserEntry(): void {
    this.vehicleForm
      .get("nameplate")
      .setValue(this.vehicleForm.value.nameplate.trim());
    if (this.vehicleForm.value.globalnameplate) {
      this.vehicleForm
        .get("globalnameplate")
        .setValue(this.vehicleForm.value.globalnameplate.trim());
    }
    if (this.vehicleForm.value.programcode) {
      this.vehicleForm
        .get("programcode")
        .setValue(this.vehicleForm.value.programcode.trim());
    }
    if (this.vehicleForm.value.performancenameplate) {
      this.vehicleForm
        .get("performancenameplate")
        .setValue(this.vehicleForm.value.performancenameplate.trim());
    }
    if (this.vehicleForm.value.crossvariantnameplate) {
      this.vehicleForm
        .get("crossvariantnameplate")
        .setValue(this.vehicleForm.value.crossvariantnameplate.trim());
    }
    if (this.vehicleForm.value.autonomousdescription) {
      this.vehicleForm
        .get("autonomousdescription")
        .setValue(this.vehicleForm.value.autonomousdescription.trim());
    }
    if (this.vehicleForm.value.autonomouslevel) {
      this.vehicleForm
        .get("autonomouslevel")
        .setValue(this.vehicleForm.value.autonomouslevel.trim());
    }
    if (!this.vehicleForm.controls.highlights.value) {
      this.vehicleForm.controls.highlights.setValue("<p></p>");
    }
    if (!this.vehicleForm.controls.powertrain.value) {
      this.vehicleForm.controls.powertrain.setValue("<p></p>");
    }
  }

  public save(close: boolean = false) {
    this.validateUserEntry();
    this.vehicleForm
      .get("createdby")
      .setValue(this.authService.userInfo.userName);
    this.vehicleForm.get("created").setValue(new Date().toISOString());
    const method: string =
      (this.data && this.data.id) || this.vehicle.id != null ? "PUT" : "POST";

    const processRecordCall = this.writerService.processRecord(
      this.vehicleForm.value,
      "vehicle",
      method
    );

    processRecordCall.subscribe({
      next: (response: any) => {
        this.vehicleForm.get("id").setValue(response.id);
        this.vehicle.id = response.id;
      },
      complete: () => {
        this.saveAdditionalRecords();
        setTimeout(() => {
          if (close) {
            this.closeDialogRef();
          }
        }, 1000);
      },
      error: (error: any) => {
        this.showErrorHandlerDialog("Error saving Vehicle Record: ", error);
        this.vehicle.id = null;
      },
    });
  }

  public closeDialogRef() {
    this.dialogRef.close();
    if (this.createMode) {
      window.location.reload();
    }
  }

  private saveAdditionalRecords() {
    const vehicleVersionCall = this.writerService.processRecord(
      this.vehicleForm.value,
      "vehicle_version",
      "POST"
    );
    vehicleVersionCall.subscribe({
      next: () => {},
      complete: () => {
        const electrification = this.loadElectrification(
          this.vehicleForm.get("id").value,
          this.vehicleForm.get("created").value
        ).filter((type) => type.type != null);
        if (electrification.length > 0) {
          this.saveElectrificationRecord(electrification);
        }
        if (this.isImage) {
          this.saveImages();
        }
        this.complete = true;
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: { message: Messages.successRefresh, status: "Success" },
          autoFocus: false,
        });
      },
      error: (error: any) => {
        this.showErrorHandlerDialog("Error saving Vehicle Version: ", error);
      },
    });
  }

  private saveImages() {
    const created = new Date().toISOString();
    const createdby = this.authService.userInfo.userName;
    const updateImages = this.writerService.uploadFiles(
      this.vehicle,
      "image",
      createdby,
      created
    );
    updateImages.subscribe({
      next: () => {},
      error: (error: any) => {
        this.showErrorHandlerDialog("Error saving Photos: ", error);
      },
    });
  }

  private saveElectrificationRecord(electrification: Electrification[]) {
    const electrificationCall = this.writerService.processRecord(
      electrification,
      "electrification",
      "POST"
    );
    electrificationCall.subscribe({
      next: () => {},
      complete: () => {
        const brandUpdate = this.writerService.updateBrandForPropulsion(
          this.vehicle.id,
          this.vehicleForm.get("brand").value
        );
        brandUpdate.subscribe({
          next: () => {},
          error: (error: any) => {
            this.showErrorHandlerDialog("Error saving Vehicle Record: ", error);
          },
        });
      },
      error: (error: any) => {
        this.showErrorHandlerDialog(
          "Error saving Electrification Record: ",
          error
        );
      },
    });
  }

  public saveAndClose() {
    this.save(true);
  }

  private showErrorHandlerDialog(message: string, error: any) {
    this.dialog.open(ErrorHandlerDialogComponent, {
      data: {
        message:
          message +
          (error.error == undefined ? error.message : error.error?.message),
        status: error.error == null ? error.status : error.error?.status,
      },
      autoFocus: false,
    });
  }

  public validateDate(): void {
    if (this.vehicleForm.get("yearbegin").value === 1111) {
      this.minStartYear = 1111;
    } else {
      this.minStartYear = 1980;
    }
    if (this.vehicleForm.get("yearbegin").value === 1112) {
      this.vehicleForm.get("yearbegin").setValue(1980);
    }
    if (this.vehicleForm.get("yearbegin").value === 1980) {
      this.minStartYear = 1979;
    }
    if (this.vehicleForm.get("yearbegin").value === 1979) {
      this.vehicleForm.get("yearbegin").setValue(1111);
      this.minStartYear = 1111;
    }
  }

  public uploadImage(fileInputEvent: any, perspective: string): void {
    this.isImage = true;
    switch (perspective) {
      case "front":
        this.vehicle.fphotofront = fileInputEvent.target.files[0];
        this.vehicle.photofront = this.vehicleForm.get("id").value
          ? this.vehicleForm.get("id").value +
            "-" +
            fileInputEvent.target.files[0].name
          : fileInputEvent.target.files[0].name;

        break;
      case "rear":
        this.vehicle.fphotorear = fileInputEvent.target.files[0];
        this.vehicle.photorear = this.vehicleForm.get("id").value
          ? this.vehicleForm.get("id").value +
            "-" +
            fileInputEvent.target.files[0].name
          : fileInputEvent.target.files[0].name;
        break;
      case "interior":
        this.vehicle.fphotointerior = fileInputEvent.target.files[0];
        this.vehicle.photointerior = this.vehicleForm.get("id").value
          ? this.vehicleForm.get("id").value +
            "-" +
            fileInputEvent.target.files[0].name
          : fileInputEvent.target.files[0].name;
        break;
      case "side":
        this.vehicle.fphotoside = fileInputEvent.target.files[0];
        this.vehicle.photoside = this.vehicleForm.get("id").value
          ? this.vehicleForm.get("id").value +
            "-" +
            fileInputEvent.target.files[0].name
          : fileInputEvent.target.files[0].name;
    }
    //Open up a dialog to ler them know the upload was successful
    this.dialog.open(ErrorHandlerDialogComponent, {
      data: {
        message:
          "File uploaded successfully, please save record before exiting",
        status: "Success",
      },
    });
  }

  public loadElectrification(vehicleId: any, created: any): Electrification[] {
    const electrification: any[] = [];
    let electric: Electrification;
    Object.entries(this.vehicleForm.value.propulsion)
      .filter((entry) => entry[1])
      .filter((entryx) => this.electricTypes.includes(entryx[0]))
      .forEach((type) => {
        electric = new Electrification();
        electric.vehicleid = vehicleId;
        electric.type = type[0];
        electric.created = created;
        electrification.push(electric);
      });
    return electrification;
  }

  public onSubmit(): void {
    console.log(this.vehicleForm.value);
    console.log(
      this.loadElectrification(
        this.vehicleForm.get("id").value,
        this.vehicleForm.get("created").value
      )
    );
  }

  public deleteRecord(): void {
    //Make sure user wants to delete
    const confirmDialog = this.dialog.open(ConfirmDeletionDialogComponent, {
      data: {
        recordId: this.vehicle.id,
        recordName: this.vehicle.brand + " " + this.vehicle.nameplate,
        recordType: "vehicle",
        vehicleId: this.vehicle.id,
      },
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isLoading = true;
        this.writerService.deleteRecord(this.vehicle.id).subscribe({
          next: () => {
            this.dialogRef.close();
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: {
                message: "Vehicle " + this.vehicle.id + " deleted successfully",
                status: "Success",
              },
            });
            this.router.navigate(["/view/vehicles"]);
          },
          error: (error: any) => {
            this.dialogRef.close();
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: {
                message: "Delete failed. Please refresh and try again",
                status: error.error == null ? error.status : error.error.status,
              },
            });
          },
        });
      }
    });
  }

  public deleteFile(filename: string, perspective: string): void {
    this.writerService.deleteFile(filename, "photos").subscribe({
      next: () => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: { message: "File deleted successfully", status: "Success" },
        });
        switch (perspective) {
          case "front":
            this.vehicle.photofront = null;
            this.vehicle.fphotofront = null;
            this.vehicle.photofronturl = null;
            break;
          case "rear":
            this.vehicle.photorear = null;
            this.vehicle.fphotorear = null;
            this.vehicle.photorearurl = null;
            break;
          case "interior":
            this.vehicle.photointerior = null;
            this.vehicle.fphotointerior = null;
            this.vehicle.photointeriorurl = null;
            break;
          case "side":
            this.vehicle.photoside = null;
            this.vehicle.fphotoside = null;
            this.vehicle.photosideurl = null;
        }
      },
      error: (error: any) => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message: "Delete failed. Please refresh and try again",
            status: error.error == null ? error.status : error.error.status,
          },
        });
      },
    });
  }

  public downloadPhoto(
    filename: string,
    displayName: string,
    event?: Event
  ): void {
    if (event) {
      event.preventDefault();
    }
    if (!filename) {
      return;
    }
    this.readerService.getPhoto(filename).subscribe({
      next: (blob) => {
        FileSaver.saveAs(blob, displayName || filename);
      },
      error: (error: any) => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message: "Download failed. Please try again.",
            status: error.error == null ? error.status : error.error.status,
          },
        });
      },
    });
  }
  public editorCreated(quill: Quill): void {
    quill.clipboard.addMatcher("TABLE", (node, delta) => {
      const Delta = Quill.import("delta");
      const tableTagStyles = node.getAttribute("style");
      return new Delta([
        {
          insert: {
            QuillTableEmbed:
              `<style>#tableId {${tableTagStyles} margin: 0 auto !important; }</style>` +
              delta.ops[0].insert.QuillTableEmbed,
          },
        },
      ]);
    });
  }

  public checkPerformance(): void {
    if (!this.vehicleForm.get("performance").value) {
      this.vehicleForm.get("performancenameplate").setValue(null);
    }
  }

  public checkCrossVariant(): void {
    if (!this.vehicleForm.get("crossvariant").value) {
      this.vehicleForm.get("crossvariantnameplate").setValue(null);
    }
  }

  public checkAutonomous(): void {
    if (!this.vehicleForm.get("isautonomous").value) {
      this.vehicleForm.get("autonomouslevel").setValue(null);
      this.vehicleForm.get("autonomousprobability").setValue(null);
      this.vehicleForm.get("autonomousyear").setValue(null);
      this.vehicleForm.get("autonomousdescription").setValue(null);
    }
  }
}
